import { LiaTransparentElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import {
   clearDeveloper, completeDevice, deviceInfo, getDeveloper, loginOptions, providerStartUrl, signOut
} from '../api/developer.js';
import { ago } from '../../shared/ui/format.js';
import { PROVIDER_ICONS } from './login.js';

/**
 * Where a client's sign-in is completed.
 *
 * `covdbg login` shows a code and this page's address, with the code already in it. The person gets here
 * in one of two ways, and the page is the same afterwards:
 *
 * - **Signed in to this browser already**: the page reads the code (`GET login/device/{code}`) and shows
 *   which client asked and when, then offers "Continue as <e-mail>" or "Use another account".
 * - **Not signed in**: the provider buttons. The provider sends the browser back to the service, which
 *   signs this browser in to the account the person chooses and lands here again on the team step.
 *
 * The **team step** lists the account's teams (personal first, then by name) as radios, the client's hint
 * preselected, and a single team is still shown rather than picked silently. Nothing is completed until
 * "Sign in" is pressed: a provider sign-in proves who the person is, not that they mean to sign in this
 * client, nor for which team. The done page says what happened and offers to sign this browser out, because
 * signing in a client leaves the browser signed in to the portal.
 *
 * The code is editable while it is not yet known, because the address may have been typed by hand from a
 * terminal, and because a code that was refused - too old, already completed - is corrected here rather
 * than by starting the client over.
 */
export class UserDevice extends LiaTransparentElement
{
   static properties = {
      code: { state: true },
      state: { state: true },
      phase: { state: true },
      options: { state: true },
      device: { state: true },
      selected: { state: true },
      chosen: { state: true },
      email: { state: true },
      another: { state: true },
      signedOut: { state: true },
      error: { state: true },
      busy: { state: true }
   };

   constructor()
   {
      super();

      const params = new URLSearchParams(location.search);

      this.code = normalize(params.get('code') ?? '');

      // The service's refusal (denied, failed, expired) or nothing.
      this.state = params.get('state') ?? '';
      this.step = params.get('step') ?? '';

      // asking (the provider buttons), loading, continue, team, done
      this.phase = 'loading';
      this.options = null;
      this.device = null;
      this.selected = '';
      this.chosen = null;
      this.email = '';
      this.another = false;
      this.signedOut = false;
      this.error = '';
      this.busy = false;
   }

   connectedCallback()
   {
      super.connectedCallback();

      loginOptions()
         .then(options => { this.options = options; })
         .catch(() => { this.options = { providers: [] }; });

      void this.start();
   }

   /** Where the page begins: with the code's details when a browser session exists, else the providers. */
   async start()
   {
      if (!getDeveloper()?.token || this.code.length !== 9 || (this.state && this.state !== 'expired'))
      {
         this.phase = 'asking';
         return;
      }

      await this.loadDevice();
   }

   async loadDevice()
   {
      this.phase = 'loading';
      this.error = '';

      try
      {
         this.device = await deviceInfo(this.code);
         this.selected = this.device.preselectedAccountId
            ?? (this.device.teams.length === 1 ? this.device.teams[0].accountId : '');
         this.phase = this.step === 'team' ? 'team' : 'continue';
      }
      catch (error)
      {
         // 401: the stored session is gone (the API already forgot it), so the providers are the way in.
         // 404: the code is not open any more.
         if (error.status === 404)
         {
            this.state = 'expired';
         }
         else if (error.status === 403 && error.details?.title === 'impersonation_not_allowed')
         {
            this.error = BORROWED;
         }
         else if (error.status !== 401)
         {
            this.error = error.message;
         }

         this.phase = 'asking';
      }
   }

   onCode(event)
   {
      this.code = normalize(event.target.value);
   }

   /** Leaves for the provider, carrying the code. The service signs the browser in and brings it back to the team step. */
   signInWith(provider)
   {
      if (this.code.length !== 9)
      {
         return;
      }

      this.busy = true;
      this.go(providerStartUrl(provider.id, { code: this.code, chooser: this.another }));
   }

   /** A page load. Its own method so a test can watch where it goes. */
   go(url)
   {
      location.assign(url);
   }

   async complete()
   {
      const team = this.device?.teams.find(entry => entry.accountId === this.selected);

      if (!team)
      {
         return;
      }

      this.busy = true;
      this.error = '';

      try
      {
         await completeDevice(this.code, team.accountId);

         this.chosen = team;
         this.email = getDeveloper()?.email ?? '';
         this.phase = 'done';
      }
      catch (error)
      {
         if (error.status === 404)
         {
            this.state = 'expired';
            this.phase = 'asking';
         }
         else if (error.status === 401)
         {
            this.phase = 'asking';
         }
         else
         {
            this.error = error.status === 403
               ? error.details?.title === 'impersonation_not_allowed'
                  ? BORROWED
                  : 'This account is not in that team. Choose another.'
               : error.message;
         }
      }
      finally
      {
         this.busy = false;
      }
   }

   async signOutHere()
   {
      this.busy = true;

      try
      {
         await signOut();
      }
      catch
      {
         // A sign-out that cannot reach the service is still a sign-out here.
      }
      finally
      {
         clearDeveloper();
         this.signedOut = true;
         this.busy = false;
      }
   }

   render()
   {
      const done = this.phase === 'done';

      return html`
         <lia-auth-layout
            .showThemeToggle=${false}
            brand="covdbg"
            brand-icon="fa-solid fa-bug"
            heading=${done ? 'Signed in' : this.phase === 'team' ? 'Choose a team' : 'Sign in your device'}
            description=${done
               ? 'The covdbg client that showed the code is signed in now.'
               : this.phase === 'team'
                  ? 'The client works for one team at a time. Choose the one it signs in for.'
                  : 'Confirm the code covdbg showed, then choose how to sign in.'}
            max-width="460"
         >
            ${this.phase === 'loading' ? this.loading : nothing}
            ${this.phase === 'asking' ? this.form : nothing}
            ${this.phase === 'continue' ? this.continueAs : nothing}
            ${this.phase === 'team' ? this.teamStep : nothing}
            ${done ? this.done : nothing}
         </lia-auth-layout>`;
   }

   get loading()
   {
      return html`
         <p class="text-body-secondary small text-center mb-0">
            <lia-spinner size="sm" class="me-2"></lia-spinner>One moment
         </p>`;
   }

   /** Which client asked, and when. What a person checks before continuing with a code they were handed. */
   get request()
   {
      const device = this.device;

      return html`
         <div class="border rounded p-3 mb-3" data-request>
            <div class="small text-body-secondary">Asked to sign in</div>
            <div class="fw-semibold">${device?.clientName || 'A covdbg client'}</div>
            <div class="small text-body-secondary">
               Started ${ago(device?.createdAt)} (code <span class="font-monospace">${this.code}</span>).
               If you did not just run <code>covdbg login</code>, close this page.
            </div>
         </div>`;
   }

   get continueAs()
   {
      const email = getDeveloper()?.email;

      return html`
         ${this.request}
         ${this.alert}
         <button class="btn btn-primary w-100 mb-2" type="button" data-continue ?disabled=${this.busy} @click=${() => { this.phase = 'team'; }}>
            Continue as ${email || 'this account'}
         </button>
         <button class="btn btn-outline-secondary w-100" type="button" data-another ?disabled=${this.busy} @click=${() => { this.another = true; this.phase = 'asking'; }}>
            Use another account
         </button>`;
   }

   get teamStep()
   {
      const teams = this.device?.teams ?? [];

      return html`
         ${this.request}
         ${this.alert}
         <div class="list-group mb-3" data-teams role="radiogroup" aria-label="Team">
            ${teams.map(team => html`
               <label class="list-group-item d-flex gap-2 align-items-start" data-team=${team.accountId}>
                  <input
                     class="form-check-input mt-1"
                     type="radio"
                     name="team"
                     .value=${team.accountId}
                     .checked=${team.accountId === this.selected}
                     ?disabled=${this.busy}
                     @change=${() => { this.selected = team.accountId; }}
                  >
                  <span>
                     <span class="fw-semibold">${team.name}</span>
                     <span class="badge text-bg-secondary ms-1">${ROLES[team.role] ?? team.role}</span>
                     ${team.role === 'owner' && !team.seatActive
                        ? html`<span class="d-block small text-body-secondary" data-no-seat>this account holds no seat here</span>`
                        : nothing}
                     ${team.accountId === this.device?.preselectedAccountId
                        ? html`<span class="d-block small text-body-secondary">Chosen by the client</span>`
                        : nothing}
                  </span>
               </label>`)}
         </div>
         <button class="btn btn-primary w-100" type="button" data-complete ?disabled=${this.busy || !this.selected} @click=${() => this.complete()}>
            ${this.busy ? html`<lia-spinner size="sm" class="me-2"></lia-spinner>Signing in` : 'Sign in this client'}
         </button>`;
   }

   get done()
   {
      return html`
         <div class="text-center py-2">
            <i class="fa-solid fa-circle-check fa-2x text-success mb-3" aria-hidden="true"></i>
            <p class="mb-1" data-done>
               <strong>${this.device?.clientName || 'The covdbg client'}</strong> is signed in as
               <strong>${this.email}</strong> for <strong>${this.chosen?.name}</strong>.
            </p>
            <p class="mb-3">You can close this tab.</p>
            <p class="text-body-secondary small mb-3">
               The client keeps the sign-in in your Windows Credential Manager; it is listed under your sessions
               at <a href="/profile/sessions">your profile</a>, where you can end it at any time.
            </p>
            ${this.signedOut
               ? html`<p class="small mb-0" data-signed-out>This browser is signed out.</p>`
               : html`
                  <p class="text-body-secondary small mb-2">Signing in the client also signed in this browser.</p>
                  <button class="btn btn-outline-secondary btn-sm" type="button" data-sign-out ?disabled=${this.busy} @click=${() => this.signOutHere()}>
                     Sign out of this browser
                  </button>`}
         </div>`;
   }

   get alert()
   {
      return this.error
         ? html`<lia-alert variant="danger" icon="fa-solid fa-triangle-exclamation" message=${this.error} class="d-block mb-3"></lia-alert>`
         : nothing;
   }

   get form()
   {
      const providers = this.options?.providers ?? [];
      const refusal = REFUSALS[this.state];

      return html`
         ${refusal
            ? html`<lia-alert variant="danger" icon="fa-solid fa-triangle-exclamation" message=${refusal} class="d-block mb-3"></lia-alert>`
            : nothing}
         ${this.alert}
         <div class="mb-3">
            <label class="form-label" for="device-code">The code covdbg showed</label>
            <input
               id="device-code"
               class="form-control form-control-lg font-monospace text-center text-uppercase"
               type="text"
               name="code"
               autocomplete="one-time-code"
               placeholder="XXXX-XXXX"
               maxlength="9"
               .value=${this.code}
               @input=${event => this.onCode(event)}
               ?disabled=${this.busy}
            >
         </div>
         ${!this.options
            ? html`
               <p class="text-body-secondary small text-center mb-0">
                  <lia-spinner size="sm" class="me-2"></lia-spinner>One moment
               </p>`
            : providers.length
               ? providers.map(provider => html`
                  <button
                     class="btn btn-primary w-100 mb-2"
                     type="button"
                     data-provider=${provider.id}
                     ?disabled=${this.busy || this.code.length !== 9}
                     @click=${() => this.signInWith(provider)}
                  >
                     <i class=${`${PROVIDER_ICONS[provider.id] ?? 'fa-solid fa-right-to-bracket'} me-2`} aria-hidden="true"></i>
                     Sign in with ${provider.name}
                  </button>`)
               : html`
                  <lia-alert
                     variant="warning"
                     icon="fa-solid fa-triangle-exclamation"
                     message="Nobody can sign in on this deployment until an identity provider is configured."
                  ></lia-alert>`}
         <p class="form-text text-center mb-0 mt-2">
            ${this.another
               ? 'You choose the account next. Then you choose the team the client signs in for.'
               : 'After this you choose the team the client signs in for. This browser is signed in too.'}
         </p>`;
   }
}

/** `xxxx-xxxx` however it was typed: upper case, hyphenated, nothing else. */
function normalize(value)
{
   const letters = String(value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);

   return letters.length > 4 ? `${letters.slice(0, 4)}-${letters.slice(4)}` : letters;
}

const BORROWED = 'This is a view an operator borrowed, which cannot sign in a client. The person it belongs to has to sign in themselves.';

const ROLES = { personal: 'Personal', owner: 'Owner', member: 'Member' };

const REFUSALS = {
   denied: 'The sign-in was refused at the provider. Nothing was signed in.',
   failed: 'The provider did not say who you are. Try again.',
   expired: 'That code is no longer open - it ran out, or it was already completed. Run `covdbg login` again for a new one.'
};

customElements.define('user-device', UserDevice);
